import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AccountAsyncSelect } from "@/features/accounting/components/AccountAsyncSelect";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PAYMENT_METHOD_TYPE,
  type CreatePaymentMethodRequest,
  type PaymentMethodType,
} from "../services/paymentMethodsApi";

interface PaymentMethodFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSubmitting?: boolean;
  canConfigureGlAccount?: boolean;
  onSubmit: (payload: CreatePaymentMethodRequest) => void;
}

const PROVIDER_SUGGESTIONS = [
  "Safaricom",
  "Equity",
  "Family Bank",
  "NCBA",
  "KCB",
  "Co-operative Bank",
  "Absa",
  "Stanbic",
];

const TYPE_OPTIONS: { value: PaymentMethodType; label: string }[] = [
  { value: PAYMENT_METHOD_TYPE.Cash, label: "Cash" },
  { value: PAYMENT_METHOD_TYPE.MpesaTill, label: "Mpesa Till" },
  { value: PAYMENT_METHOD_TYPE.MpesaPaybill, label: "Mpesa Paybill" },
  { value: PAYMENT_METHOD_TYPE.Bank, label: "Bank Account" },
];

export const PaymentMethodForm = ({
  open,
  onOpenChange,
  isSubmitting,
  canConfigureGlAccount = false,
  onSubmit,
}: PaymentMethodFormProps) => {
  const [type, setType] = useState<PaymentMethodType>(PAYMENT_METHOD_TYPE.Cash);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [tillNumber, setTillNumber] = useState("");
  const [paybillNumber, setPaybillNumber] = useState("");
  const [mpesaAccountNumber, setMpesaAccountNumber] = useState("");
  const [provider, setProvider] = useState("");
  const [bankAccountId, setBankAccountId] = useState("");
  const [glAccountId, setGlAccountId] = useState<string>();

  const handleSubmit = () => {
    if (!name.trim() || !code.trim()) return;

    const payload: CreatePaymentMethodRequest = {
      name: name.trim(),
      code: code.trim(),
      type,
      glAccountId,
    };

    if (type === PAYMENT_METHOD_TYPE.MpesaTill) {
      payload.tillNumber = tillNumber.trim();
      payload.provider = provider.trim();
    } else if (type === PAYMENT_METHOD_TYPE.MpesaPaybill) {
      payload.paybillNumber = paybillNumber.trim();
      payload.mpesaAccountNumber = mpesaAccountNumber.trim() || undefined;
      payload.provider = provider.trim();
    } else if (type === PAYMENT_METHOD_TYPE.Bank) {
      payload.bankAccountId = bankAccountId.trim();
    }

    onSubmit(payload);
  };

  const submitDisabled =
    isSubmitting ||
    !name.trim() ||
    !code.trim() ||
    (type === PAYMENT_METHOD_TYPE.MpesaTill &&
      (!tillNumber.trim() || !provider.trim())) ||
    (type === PAYMENT_METHOD_TYPE.MpesaPaybill &&
      (!paybillNumber.trim() || !provider.trim())) ||
    (type === PAYMENT_METHOD_TYPE.Bank && !bankAccountId.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Submit Payment Method</DialogTitle>
          <DialogDescription>
            Submit a new Cash, Mpesa, or Bank account. The General Manager must
            approve before it can be used to record payments.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="space-y-2">
            <Label htmlFor="pm-type">Type</Label>
            <Select
              value={String(type)}
              onValueChange={(v) => setType(Number(v) as PaymentMethodType)}
            >
              <SelectTrigger id="pm-type">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
            <div className="space-y-2">
              <Label htmlFor="pm-name">Name</Label>
              <Input
                id="pm-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Equity Till - Main Gate"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pm-code">Code</Label>
              <Input
                id="pm-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="e.g. EQ-TILL-MAIN"
              />
            </div>
          </div>

          {type === PAYMENT_METHOD_TYPE.MpesaTill && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
              <div className="space-y-2">
                <Label htmlFor="pm-till">Till Number</Label>
                <Input
                  id="pm-till"
                  value={tillNumber}
                  onChange={(e) => setTillNumber(e.target.value)}
                  placeholder="e.g. 4321001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-provider">Provider</Label>
                <Input
                  id="pm-provider"
                  list="pm-provider-suggestions"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  placeholder="Equity / NCBA / Safaricom"
                />
                <datalist id="pm-provider-suggestions">
                  {PROVIDER_SUGGESTIONS.map((p) => (
                    <option key={p} value={p} />
                  ))}
                </datalist>
              </div>
            </div>
          )}

          {type === PAYMENT_METHOD_TYPE.MpesaPaybill && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
                <div className="space-y-2">
                  <Label htmlFor="pm-paybill">Paybill Number</Label>
                  <Input
                    id="pm-paybill"
                    value={paybillNumber}
                    onChange={(e) => setPaybillNumber(e.target.value)}
                    placeholder="e.g. 880100"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pm-paybill-provider">Provider</Label>
                  <Input
                    id="pm-paybill-provider"
                    list="pm-provider-suggestions"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                    placeholder="Equity / Family Bank / NCBA"
                  />
                  <datalist id="pm-provider-suggestions">
                    {PROVIDER_SUGGESTIONS.map((p) => (
                      <option key={p} value={p} />
                    ))}
                  </datalist>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pm-acct">Account Number Pattern (optional)</Label>
                <Input
                  id="pm-acct"
                  value={mpesaAccountNumber}
                  onChange={(e) => setMpesaAccountNumber(e.target.value)}
                  placeholder="e.g. INV-{invoiceNumber}"
                />
              </div>
            </>
          )}

          {type === PAYMENT_METHOD_TYPE.Bank && (
            <div className="space-y-2">
              <Label htmlFor="pm-bank">Bank Account ID</Label>
              <Input
                id="pm-bank"
                value={bankAccountId}
                onChange={(e) => setBankAccountId(e.target.value)}
                placeholder="UUID of the registered bank account"
              />
              <p className="text-muted-foreground text-xs">
                Pick from the existing Bank Accounts registry. Select the posting GL
                account separately below.
              </p>
            </div>
          )}

          {canConfigureGlAccount && (
            <div className="space-y-2">
              <Label>Posting GL Account (optional)</Label>
              <AccountAsyncSelect
                valueId={glAccountId}
                onChange={setGlAccountId}
                postingOnly
                activeOnly
                includeAllOption={false}
              />
              <p className="text-muted-foreground text-xs">
                Select the Asset account debited when this channel receives money.
                If omitted, the global Cash, Bank, or M-Pesa posting setting is used.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            {isSubmitting ? "Submitting..." : "Submit for Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
